import { AlertTriangle, CreditCard, LifeBuoy, PauseCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useCreditLimitStatus } from "@/hooks/useCreditLimitStatus";
import { creditBanner, formatCop } from "@/lib/credit-limit";

export function CreditLimitBanner() {
  const { activeWorkspaceId } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const { status } = useCreditLimitStatus(activeWorkspaceId);
  const banner = creditBanner(status);
  if (!banner) return null;

  const goToAccount = () => navigate("/app/settings/account");

  if (banner.kind === "warning") {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" />
          <p className="text-amber-700 dark:text-amber-400 font-medium">
            Llevas el {banner.percent} % de tu tope de crédito: {formatCop(banner.exposure)} sin pagar de {formatCop(banner.creditLimit)}.
            {" "}Al superarlo, la IA queda en pausa hasta que pagues lo consumido.
          </p>
        </div>
        <button onClick={goToAccount} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors">
          <CreditCard className="h-3.5 w-3.5" aria-hidden="true" /> Ver mi cuenta
        </button>
      </div>
    );
  }

  if (banner.kind === "override") {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/10 px-4 py-2.5 flex items-center gap-2 text-sm">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" />
        <p className="text-amber-700 dark:text-amber-400 font-medium">
          Superaste tu tope de crédito ({formatCop(banner.exposure)} sin pagar de {formatCop(banner.creditLimit)}). La IA sigue activa por una
          {" "}habilitación temporal hasta el {format(parseISO(banner.overrideUntil), "d 'de' MMMM", { locale: es })}.
        </p>
      </div>
    );
  }

  return (
    <div role="alert" className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <PauseCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
        <p className="text-destructive font-medium">
          <span className="font-bold">La IA está en pausa</span>
          {" — "}
          Tu consumo sin pagar ({formatCop(banner.exposure)}) superó tu tope de crédito de {formatCop(banner.creditLimit)}.
          {banner.canPay
            ? " Paga lo consumido y la IA se reactiva en cuanto se confirme el pago."
            : ` Es el consumo del ciclo en curso (${formatCop(banner.currentAmount)}), que se cobra al cierre del ciclo. Escríbenos para revisar tu tope.`}
        </p>
      </div>
      {banner.canPay ? (
        <button onClick={goToAccount} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors">
          <CreditCard className="h-3.5 w-3.5" aria-hidden="true" /> Pagar lo consumido
        </button>
      ) : (
        <a
          href={`mailto:${brand.supportEmail}?subject=${encodeURIComponent("Tope de crédito")}`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
        >
          <LifeBuoy className="h-3.5 w-3.5" aria-hidden="true" /> Contactar a soporte
        </a>
      )}
    </div>
  );
}
